import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SOURCE_COLORS, formatMoney } from "../lib/constants";
import BuildingProfileNotice from "./BuildingProfileNotice";

/**
 * Compares trade performance by source (Own Analysis vs Signal vs Influencer vs Copy).
 * Renders a stat row per source with horizontal bar showing relative P&L impact.
 * Highlights the BEST source in green and WORST in red. Shows a dynamic one-line insight.
 */
export default function SignalVsSelf({
  stats, currency,
  lowSample = false, tradesRemaining = 0,
}) {
  const nav = useNavigate();
  const items = stats?.by_source || [];

  const { rows, insight, insightTone, bestKey, worstKey } = useMemo(() => {
    if (!items.length)
      return { rows: [], insight: "", insightTone: "neutral", bestKey: null, worstKey: null };

    const pnlField = currency === "INR" ? "total_pnl_inr" : "total_pnl_usd";
    const sortedByPnl = [...items].sort((a, b) => b[pnlField] - a[pnlField]);
    const best = sortedByPnl[0];
    const worst = sortedByPnl[sortedByPnl.length - 1];

    const maxAbs = items.reduce((m, it) => Math.max(m, Math.abs(it[pnlField])), 0);

    // ── Iter 13: hero dynamic insight ──
    // Rule 1: any source with < 5 trades → not enough data (gray)
    // Rule 2: Own Analysis win_rate > MAX(signal-source win_rates) → green endorsement
    // Rule 3: any signal source win_rate > Own Analysis win_rate → red warning
    const own = items.find((it) => it.source === "Own Analysis");
    const signalSources = items.filter((it) => it.source !== "Own Analysis");
    const allHaveEnoughData = items.every((it) => it.count >= 5);

    let line = "";
    let tone = "neutral";  // neutral | positive | warning
    if (!allHaveEnoughData) {
      line = "📊 Add more trades to unlock your Signal vs Self insights.";
      tone = "neutral";
    } else if (!own) {
      // No Own Analysis trades — just describe the best signal source
      const top = [...items].sort((a, b) => b.win_rate - a.win_rate)[0];
      line = `Your ${top.source} trades have a ${(top.win_rate ?? 0).toFixed(2)}% win rate. Log Own Analysis trades to compare.`;
      tone = "neutral";
    } else {
      const maxSignalWR = signalSources.length
        ? Math.max(...signalSources.map((s) => s.win_rate))
        : -1;
      if (own.win_rate > maxSignalWR) {
        line = "✅ Your own analysis outperforms all signals. Keep trusting yourself.";
        tone = "positive";
      } else {
        line = "⚠️ You are performing better on signals than your own analysis. Review your strategy.";
        tone = "warning";
      }
    }
    // ────────────────────────────────────

    const rows = items.map((it) => ({
      ...it,
      pnl: it[pnlField],
      width: maxAbs > 0 ? Math.max(4, (Math.abs(it[pnlField]) / maxAbs) * 100) : 4,
    }));

    return {
      rows,
      insight: line,
      insightTone: tone,
      bestKey: best?.source || null,
      worstKey: worst?.source || null,
    };
  }, [items, currency]);

  if (!items.length) {
    return (
      <div
        data-testid="signal-vs-self"
        className="bg-[var(--bt-surface)] border border-[var(--bt-border)] rounded-sm p-5"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="bt-label">Signal vs Self</p>
            <h2 className="font-heading text-2xl mt-1 flex items-center gap-2">
              Performance by Source
            </h2>
          </div>
        </div>
        <p className="text-sm text-[var(--bt-text-muted)] text-center py-6">
          Log trades with a Source tag to see whether your own analysis or signal groups perform better.
        </p>
      </div>
    );
  }

  // ── Iter 36: Guardrail — until the user has enough trades, the
  // per-source win-rates / P&L numbers are too noisy to be useful.
  if (lowSample) {
    return (
      <div
        data-testid="signal-vs-self"
        className="bg-[var(--bt-surface)] border border-[var(--bt-border)] rounded-sm p-5"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="bt-label">Signal vs Self</p>
            <h2 className="font-heading text-2xl mt-1 flex items-center gap-2">
              Performance by Source
            </h2>
          </div>
        </div>
        <BuildingProfileNotice
          tradesRemaining={tradesRemaining}
          testId="signal-vs-self-placeholder"
        />
      </div>
    );
  }

  return (
    <div
      data-testid="signal-vs-self"
      className="bg-[var(--bt-surface)] border border-[var(--bt-border)] rounded-sm p-5"
    >
      {/* ── Iter 13: Hero section header with green left border ── */}
      <div
        data-testid="signal-vs-self-header"
        className="flex items-start justify-between mb-1 pl-4 relative"
      >
        <div
          aria-hidden
          className="absolute left-0 top-0 bottom-0"
          style={{ width: "4px", background: "#00ff88", borderRadius: "2px" }}
        />
        <div>
          <p
            className="text-[11px] font-bold tracking-[0.18em] uppercase"
            style={{ color: "#00ff88" }}
          >
            ⚡ Signal vs Self Tracker — Your Edge Analysis
          </p>
          <h2 className="font-heading text-2xl mt-1 flex items-center gap-2 text-white">
            Performance by Source
          </h2>
        </div>
        <span className="text-xs text-[var(--bt-text-muted)] font-mono">
          in {currency === "INR" ? "₹ INR" : "$ USD"}
        </span>
      </div>

      {/* Dynamic insight banner — color depends on tone */}
      <p
        data-testid="signal-vs-self-insight"
        data-tone={insightTone}
        className="mt-4 mb-6 text-sm leading-relaxed border rounded-md px-4 py-3"
        style={{
          color:
            insightTone === "positive" ? "#00ff88"
            : insightTone === "warning" ? "#ef4444"
            : "#94a3b8",
          background:
            insightTone === "positive" ? "rgba(0, 255, 136, 0.06)"
            : insightTone === "warning" ? "rgba(239, 68, 68, 0.06)"
            : "rgba(148, 163, 184, 0.05)",
          borderColor:
            insightTone === "positive" ? "rgba(0, 255, 136, 0.25)"
            : insightTone === "warning" ? "rgba(239, 68, 68, 0.25)"
            : "rgba(148, 163, 184, 0.18)",
        }}
      >
        {insight}
      </p>

      <ul className="space-y-4">
        {rows.map((r) => {
          const color = SOURCE_COLORS[r.source] || "#888";
          const isBest = r.source === bestKey && r.pnl > 0;
          const isWorst = r.source === worstKey && r.pnl < 0 && rows.length > 1;
          const highlightColor = isBest ? "#00E5A0" : isWorst ? "#FF3B30" : color;
          return (
            <li
              key={r.source}
              data-testid={`signal-row-${r.source}`}
              role="button"
              onClick={() => nav(`/trades?source=${encodeURIComponent(r.source)}`)}
              className="cursor-pointer hover:bg-[var(--bt-surface-hover)] -mx-2 px-2 py-2 rounded-sm transition-colors duration-150"
            >
              <div className="flex items-center justify-between mb-2 gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ background: color }}
                  />
                  <span className="font-semibold text-sm truncate">{r.source}</span>
                  {isBest && (
                    <span
                      data-testid={`badge-best`}
                      className="px-1.5 py-0.5 text-[10px] font-black uppercase tracking-wider rounded-sm shrink-0"
                      style={{ background: "rgba(0,229,160,0.15)", color: "#00E5A0", border: "1px solid rgba(0,229,160,0.4)" }}
                    >
                      Best
                    </span>
                  )}
                  {isWorst && (
                    <span
                      data-testid={`badge-worst`}
                      className="px-1.5 py-0.5 text-[10px] font-black uppercase tracking-wider rounded-sm shrink-0"
                      style={{ background: "rgba(255,59,48,0.15)", color: "#FF3B30", border: "1px solid rgba(255,59,48,0.4)" }}
                    >
                      Worst
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0 text-xs">
                  <span className="text-[var(--bt-text-muted)] font-mono">
                    {r.count} · {(r.win_rate ?? 0).toFixed(2)}%
                  </span>
                  <span
                    className="bt-num font-bold"
                    style={{ color: r.pnl >= 0 ? "#00E5A0" : "#FF3B30", minWidth: "80px", textAlign: "right" }}
                  >
                    {formatMoney(r.pnl, currency)}
                  </span>
                  <ArrowRight className="w-3.5 h-3.5 text-[var(--bt-text-muted)]" />
                </div>
              </div>
              <div className="h-2 bg-[var(--bt-surface-hover)] rounded-sm overflow-hidden">
                <div
                  className="h-full transition-all duration-700"
                  style={{
                    width: `${r.width}%`,
                    background: highlightColor,
                    boxShadow: isBest ? "0 0 12px rgba(0,229,160,0.5)" : isWorst ? "0 0 12px rgba(255,59,48,0.4)" : "none",
                  }}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
